import React, {HTMLInputTypeAttribute, ReactNode, useEffect, useState} from "react";
import {ComposedQuestion} from "../models/definitions/ComposedQuestion";
import {
	Alert,
	AlertTitle,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormLabel,
	Grid,
	InputAdornment,
	InputLabel,
	MenuItem, Radio, RadioGroup, Select,
	Stack,
	Step,
	StepButton,
	Stepper,
	TextField, Tooltip,
	Typography
} from "@mui/material";
import {DynamicFormsExpression} from "../models/expressions/DynamicFormsExpression";
import MuiPhoneNumber from "material-ui-phone-number";
import {DesktopDatePicker} from "@mui/x-date-pickers";
import {DatePicker} from "../../../components/common/input/DatePicker";
import {ExpressionEngine} from "../models/expressions/ExpressionEngine";
import {GlobalState} from "../../../global/GlobalState";
import {renderLines, renderText} from "../../../components/common/TextRenderer";
import {ComposedForm} from "../models/definitions/ComposedForm";
import {FormLayout} from "../models/definitions/FormLayout";
import {LayoutStep} from "../models/definitions/LayoutStep";
import {LayoutElement} from "../models/definitions/LayoutElement";
import {LayoutGroupElement} from "../models/definitions/LayoutGroupElement";
import {LayoutLabelElement} from "../models/definitions/LayoutLabelElement";
import {LayoutQuestionElement} from "../models/definitions/LayoutQuestionElement";
import {LayoutGenericElement} from "../models/definitions/LayoutGenericElement";
import {Info, Lightbulb} from "@mui/icons-material";
import {AlertColor} from "@mui/material/Alert/Alert";
import {FormVisitor} from "../models/visitors/FormVisitor";
import {NameExtractor} from "../models/visitors/NameExtractor";
import {DefaultRuntimeContext} from "../models/runtime/RuntimeContext";
import {RuntimeString} from "../models/runtime/RuntimeString";
import {LocalDate} from "@js-joda/core";
import {LayoutCustomProperties} from "../models/definitions/LayoutCustomProperties";

export class FormData {
	readonly warnings: Set<string> = new Set<string>();
	readonly questionIndex: Map<string, ComposedQuestion> = new Map<string, ComposedQuestion>();
	
	initialValues: any;
	
	constructor(
		readonly composedForm: ComposedForm | null,
		initialValues: object = {},
	) {
		this.initialValues = initialValues;
		composedForm?.questions.forEach(question => {
			this.questionIndex.set(question.name, question);
		});
	}
	
	findQuestion(name: string): ComposedQuestion | undefined {
		return this.questionIndex.get(name);
	}
	
	addWarning(message: string) {
		if (!this.warnings.has(message))
			console.warn(message);
		this.warnings.add(message);
	}
	
	// todo make this into a dynamic object storage as currently, it doesnt handle json paths correctly
	readonly values = new Map<string, string>();
	readonly valueChangeListeners = new Map<string, ((name: string, value: string | null | undefined) => void)[]>();
	
	setValue(reference: string | undefined, paths: string[], value: string | null | undefined) {
		if (value === undefined || value === null) {
			paths.forEach(path => this.values.delete(path));
			if (reference)
				this.values.delete(reference);
		} else {
			paths.forEach(path => this.values.set(path, value));
			if (reference)
				this.values.set(reference, value);
		}
		
		if (reference) {
			this.valueChangeListeners.get(reference)?.forEach(listener => {
				listener(reference, value);
			});
		}
	}
	
	findValueByPath(path: string): string | undefined {
		return this.values.get(path);
	}
	
	findValueByName(reference: string): string | undefined {
		return this.values.get(reference) ?? this.initialValues[reference];
	}
	
	onValueChange(reference: string, handler: (name: string, value: string | null | undefined) => void) {
		let listener = this.valueChangeListeners.get(reference);
		if (listener === undefined) {
			listener = [];
			this.valueChangeListeners.set(reference, listener);
		}
		listener.push(handler);
	}
}

function append(...nodes: string[]): string {
	return nodes.join(".");
}

// noinspection JSMethodCanBeStatic
export class FormsRenderer {
	// todo user settings/localization
	
	private expressionEngine = new ExpressionEngine({
		debug: false,
		// isDebug: expression => {
		// 	return expression.function !== "literal";
		// },
	});
	
	verifyExpressions(form: ComposedForm) {
		new FormVisitor().walkForm(form);
	}
	
	renderForm(form: ComposedForm, formData: FormData): JSX.Element {
		this.verifyExpressions(form);
		return <RenderForm form={form} renderer={this} formData={formData}/>;
	}
	
	renderQuestion(question: ComposedQuestion, formData: FormData): JSX.Element {
		return <RenderQuestion question={question} renderer={this} formData={formData} layoutCustom={{}}/>;
	}
	
	evaluateBooleanExpression(expression: DynamicFormsExpression, formData: FormData, currentValue: string | null): boolean {
		try {
			const value = this.expressionEngine.evaluate(expression, new DefaultRuntimeContext(formData, currentValue ? new RuntimeString(currentValue) : null));
			return value?.asBoolean() ?? false;
		} catch (e) {
			return false;
		}
	}
	
	evaluateStringExpression(expression: DynamicFormsExpression, formData: FormData, currentValue: string | null): string {
		try {
			const value = this.expressionEngine.evaluate(expression, new DefaultRuntimeContext(formData, currentValue ? new RuntimeString(currentValue) : null));
			return value?.asString() ?? "";
		} catch (e) {
			return "";
		}
	}
}

function RenderForm({form, renderer, formData}: {form: ComposedForm; renderer: FormsRenderer; formData: FormData}): JSX.Element {
	return RenderLayout(form.layout, renderer, formData);
}

function RenderLayout(layout: FormLayout, renderer: FormsRenderer, formData: FormData): JSX.Element {
	const [step, setStep] = useState(0);
	
	return <>
		<Stepper nonLinear activeStep={step}>
			{layout.steps.map((step, index) => (
				<Step key={index.toString()}>
					<StepButton color="inherit" onClick={() => setStep(index)}>
						{step.name}
					</StepButton>
				</Step>
			))}
		</Stepper>
		
		<RenderLayoutStep step={layout.steps[step]} renderer={renderer} formData={formData} path={append("steps", step.toString())}/>
	</>;
}

function RenderLayoutStep({step, renderer, formData, path}: {step: LayoutStep; renderer: FormsRenderer; formData: FormData; path: string}): JSX.Element {
	return <Stack id={path} key={path} spacing={2}>
		{step.elements.map((it, index) => renderLayoutElement(it, renderer, formData, append(path, "elements", index.toString())))}
	</Stack>;
}

function renderLayoutElement(element: LayoutElement, renderer: FormsRenderer, formData: FormData, path: string): ReactNode | null {
	// return RenderLayoutElement({element, renderer, formData, path});
	return <RenderLayoutElement element={element} renderer={renderer} formData={formData} path={path}/>;
}

function RenderLayoutElement({element, renderer, formData, path}: {element: LayoutElement; renderer: FormsRenderer; formData: FormData; path: string}) {
	const [referencedData, setReferencedData] = useState<object>({});
	
	useEffect(() => {
		if (element.visibleExpression) {
			const references = NameExtractor.extractNames(element.visibleExpression);
			references.map(it =>
				formData.onValueChange(it, (name, value) =>
					setReferencedData({...referencedData, [name]: value})));
		}
	});
	
	if (element.visibleExpression) {
		if (!renderer.evaluateBooleanExpression(element.visibleExpression, formData, null))
			return null;
	}
	
	if (element.type === "group") return renderLayoutGroupElement(element as LayoutGroupElement, renderer, formData, path);
	if (element.type === "label") return renderLayoutLabelElement(element as LayoutLabelElement, renderer, formData, path);
	if (element.type === "question") return renderLayoutQuestionElement(element as LayoutQuestionElement, renderer, formData, path);
	if (element.type === "generic") return renderLayoutGenericElement(element as LayoutGenericElement, renderer, formData, path);
	
	return <div id={path} key={path}>unknown element type '{element.type}'</div>;
}

function renderLayoutGroupElement(element: LayoutGroupElement, renderer: FormsRenderer, formData: FormData, path: string): JSX.Element {
	switch (element.component) {
		case "transitive":
			return renderLayoutGroupElementTransitive(element, renderer, formData, path);
		case "grid":
			return renderLayoutGroupElementGrid(element, renderer, formData, path);
		case "list":
		case undefined:
		case null:
			return renderLayoutGroupElementList(element, renderer, formData, path);
		default:
			formData.addWarning(`Unknown group element component '${element.component}'. At path '${path}'.`);
			return renderLayoutGroupElementUnknown(element, renderer, formData, path);
	}
}

function renderLayoutGroupElementTransitive(element: LayoutGroupElement, renderer: FormsRenderer, formData: FormData, path: string): JSX.Element {
	return <>
		{element.elements.map((it, index) =>
			renderLayoutElement(it, renderer, formData, append(path, "elements", index.toString())))}
	</>;
}

function renderLayoutGroupElementGrid(element: LayoutGroupElement, renderer: FormsRenderer, formData: FormData, path: string): JSX.Element {
	const columns = element.custom?.columns ?? 12;
	const width = 12 / columns;
	return <Grid container id={path} key={path} spacing={2}>
		{element.elements.map((it, index) =>
			<Grid item key={index.toString()} xs={width}>
				{renderLayoutElement(it, renderer, formData, append(path, "elements", index.toString()))}
			</Grid>)}
	</Grid>;
}

function renderLayoutGroupElementList(element: LayoutGroupElement, renderer: FormsRenderer, formData: FormData, path: string): JSX.Element {
	return <Stack id={path} key={path} spacing={2} direction={getListDirection(element)} justifyContent={"space-between"}>
		{element.elements.map((it, index) => renderLayoutElement(it, renderer, formData, append(path, "elements", index.toString())))}
	</Stack>;
}

function getListDirection(element: LayoutGroupElement): "row" | "column" | "row-reverse" | "column-reverse" {
	const direction = element.custom?.direction;
	if (direction)
		return direction;
	return "column";
}

function renderLayoutGroupElementUnknown(element: LayoutGroupElement, renderer: FormsRenderer, formData: FormData, path: string): JSX.Element {
	return <Stack id={path} key={path} spacing={2}>
		{element.elements.map((it, index) => renderLayoutElement(it, renderer, formData, append(path, "elements", index.toString())))}
	</Stack>;
}

function renderLayoutLabelElement(element: LayoutLabelElement, renderer: FormsRenderer, formData: FormData, path: string): JSX.Element {
	const text = element.textExpression
		? renderer.evaluateStringExpression(element.textExpression, formData, null)
		: element.text;
	
	// usp is allowed to have no text
	if (!text && element.component !== "usp") {
		formData.addWarning(`Missing label element text '${element.component}'. At path '${path}'.`);
	}
	
	switch (element.component) {
		case "heading":
			return renderLayoutLabelElementHeader(element, text, formData, path);
		case "warning":
			return renderLayoutLabelElementWarning(element, text, formData, path);
		case "info-banner":
			return renderLayoutLabelElementInfoBanner(element, text, formData, path);
		case "usp":
			return renderLayoutLabelElementUsp(element, text, formData, path);
		case undefined:
		case null:
			return renderLayoutLabelElementDefault(element, text, formData, path);
		default:
			formData.addWarning(`Unknown label element component '${element.component}'. At path '${path}'.`);
			return renderLayoutLabelElementUnknown(element, text, formData, path);
	}
}

function renderLayoutLabelElementHeader(element: LayoutLabelElement, text: string | null, formData: FormData, path: string): JSX.Element {
	return <Typography id={path} key={path} variant={"h6"}>{text}</Typography>;
}

function renderLayoutLabelElementWarning(element: LayoutLabelElement, text: string | null, formData: FormData, path: string): JSX.Element {
	return renderLayoutLabelElementAlert(element, text, "warning", undefined, formData, path);
}

function renderLayoutLabelElementInfoBanner(element: LayoutLabelElement, text: string | null, formData: FormData, path: string): JSX.Element {
	return renderLayoutLabelElementAlert(element, text, "info", <Lightbulb fontSize="inherit" />, formData, path);
}

function renderLayoutLabelElementAlert(
	element: LayoutLabelElement,
	text: string | null,
	severity: AlertColor | undefined,
	icon: ReactNode | undefined,
	formData: FormData,
	path: string,
): JSX.Element {
	// inline-block allows it to be sized based on content rather than taking full width
	return <div id={path} key={path} style={{display: "inline-block"}}>
		<Alert severity={severity} icon={icon}>
			<AlertTitle>{element.custom?.title}</AlertTitle>
			{renderText(text)}
		</Alert>
	</div>;
}

function renderLayoutLabelElementUsp(element: LayoutLabelElement, text: string | null, formData: FormData, path: string): JSX.Element {
	const title = element.custom?.title;
	const items = element.custom?.items ?? [];
	const bottomText = element.custom?.bottomText;
	// const variant = element.custom?.variant; // todo switch "membershipUsp"
	
	// inline-block allows it to be sized based on content rather than taking full width
	return <div id={path} key={path} style={{display: "inline-block"}}>
		<Alert severity={"info"}>
			<AlertTitle>{title}</AlertTitle>
			{items.length > 0
				? <ul>
					{items.map(it => <li>{it}</li>)}
				</ul>
				: undefined}
			{bottomText ? <p>{bottomText}</p> : undefined}
		</Alert>
	</div>;
}

function renderLayoutLabelElementDefault(element: LayoutLabelElement, text: string | null, formData: FormData, path: string): JSX.Element {
	return <div id={path} key={path}>{text}</div>;
}

function renderLayoutLabelElementUnknown(element: LayoutLabelElement, text: string | null, formData: FormData, path: string): JSX.Element {
	return <div id={path} key={path}>
		label ({text})
		<pre>{JSON.stringify(element, null, 4)}</pre>
	</div>;
}

function renderLayoutQuestionElement(element: LayoutQuestionElement, renderer: FormsRenderer, formData: FormData, path: string): JSX.Element | null {
	return <RenderLayoutQuestionElement element={element} renderer={renderer} formData={formData} path={path}/>;
}

function RenderLayoutQuestionElement({element, renderer, formData, path}: {element: LayoutQuestionElement; renderer: FormsRenderer; formData: FormData; path: string}): JSX.Element | null {
	const question = formData.findQuestion(element.name);
	
	const [referencedData, setReferencedData] = useState<object>({});
	
	useEffect(() => {
		if (question && question.visibleExpression) {
			const references = NameExtractor.extractNames(question.visibleExpression);
			references.map(it =>
				formData.onValueChange(it, (name, value) =>
					setReferencedData({...referencedData, [name]: value})));
		}
	});
	
	if (!question) {
		formData.addWarning(`Unknown question reference '${element.name}'. At path '${path}'.`);
		return null;
	}
	
	if (question.visibleExpression && !renderer.evaluateBooleanExpression(question.visibleExpression, formData, null)) {
		return null;
	}
	
	return <div id={path} key={path} style={{flexGrow: questionFlexGrow(element)}}>
		<RenderQuestion question={question} renderer={renderer} formData={formData} layoutCustom={element.custom ?? {}} component={element.component}/>
	</div>;
}

function questionFlexGrow(element: LayoutQuestionElement) {
	const grow = element.custom?.grow;
	if (grow)
		return grow;
	return undefined;
}

function renderLayoutGenericElement(element: LayoutGenericElement, renderer: FormsRenderer, formData: FormData, path: string): JSX.Element {
	switch (element.component) {
		case "horizontal-rule":
			return renderLayoutGenericElementHorizontalRule(element, renderer, formData, path);
		default:
			formData.addWarning(`Unknown generic element component '${element.component}'. At path '${path}'.`);
			return renderLayoutGenericElementUnknown(element, renderer, formData, path);
	}
}

function renderLayoutGenericElementHorizontalRule(element: LayoutGenericElement, renderer: FormsRenderer, formData: FormData, path: string): JSX.Element {
	return <div id={path} key={path}>
		<hr/>
	</div>;
}

function renderLayoutGenericElementUnknown(element: LayoutGenericElement, renderer: FormsRenderer, formData: FormData, path: string): JSX.Element {
	return <div id={path} key={path}>
		generic
		<pre>{JSON.stringify(element, null, 4)}</pre>
	</div>;
}

function RenderQuestion({question, renderer, formData, layoutCustom, component}: {question: ComposedQuestion; renderer: FormsRenderer; formData: FormData; layoutCustom: LayoutCustomProperties; component?: string | null}): JSX.Element {
	switch (component) {
		case "checkbox":
			return renderCheckBox(question, renderer, formData, layoutCustom);
		case "datepicker":
			return RenderDate(question, renderer, formData, layoutCustom);
		case "dropdown":
			return <RenderDropdown question={question} renderer={renderer} formData={formData} layoutCustom={layoutCustom}/>;
		case "integer":
			return DynamicFormsTextField({question, renderer, formData, layoutCustom, inputType: "number"});
		case "radiobuttonlist":
			return RenderRadioButtonList({question, renderer, formData, layoutCustom});
		case "text":
			return DynamicFormsTextField({question, renderer, formData, layoutCustom});
		case "textarea":
			return DynamicFormsTextField({question, renderer, formData, layoutCustom, multiline: true});
	}
	
	if (question.valueOptions != null && question.valueOptions.length > 0)
		return <RenderDropdown question={question} renderer={renderer} formData={formData} layoutCustom={layoutCustom}/>;
	
	switch (question.valueType) {
		case "boolean":
			return renderCheckBox(question, renderer, formData, layoutCustom);
		case "date":
			return RenderDate(question, renderer, formData, layoutCustom);
		case "time":
			return renderTime(question, renderer, formData, layoutCustom);
		case "datetime":
			return renderDateTime(question, renderer, formData, layoutCustom);
		case "phoneNumber":
			return renderPhoneNumber(question, renderer, formData, layoutCustom);
		default:
			return DynamicFormsTextField({question, renderer, formData, layoutCustom});
	}
}

function RenderDate(question: ComposedQuestion, renderer: FormsRenderer, formData: FormData, layoutCustom: LayoutCustomProperties): JSX.Element {
	const [selectedValue, setSelectedValue] = useState(() => getDefaultValue(question, formData) ?? null);
	const [referencedData, setReferencedData] = useState<object>({});
	
	useEffect(() => {
		const references = NameExtractor.extractNames(question);
		references.map(it =>
			formData.onValueChange(it, (name, value) =>
				setReferencedData({...referencedData, [name]: value})));
		
		// todo unregister
	});
	
	return <Row id={question.name}>
		<DatePicker
			label={question.label}
			variant={"standard"}
			value={selectedValue ? LocalDate.parse(selectedValue) : null}
			inputFormat={GlobalState.userPreferences.datePatternOrDefault}
			onValueChange={value => {
				const valueString = value?.toString() ?? null;
				setSelectedValue(valueString);
				
				formData.setValue(question.name, question.mapping, valueString);
			}}
			placeholder={question.defaultValue}
			disabled={!renderer.evaluateBooleanExpression(question.enabledExpression, formData, null)}
		/>
	</Row>;
}

function renderTime(question: ComposedQuestion, renderer: FormsRenderer, formData: FormData, layoutCustom: LayoutCustomProperties): JSX.Element {
	return <Row id={question.name}>
		<DesktopDatePicker
			label={question.label}
			inputFormat="DD/MM/YYYY"
			toolbarPlaceholder={question.defaultValue ?? ""}
			value={question.defaultValue ?? ""}
			disabled={!renderer.evaluateBooleanExpression(question.enabledExpression, formData, null)}
			renderInput={(params) => <TextField {...params} />}
			onChange={() => null}
			InputProps={{
				placeholder: "test"
			}}
		/>
	</Row>;
}

function renderDateTime(question: ComposedQuestion, renderer: FormsRenderer, formData: FormData, layoutCustom: LayoutCustomProperties): JSX.Element {
	return <Row id={question.name}>
		<DatePicker
			label={question.label}
			inputFormat="DD/MM/YYYY"
			placeholder={question.defaultValue ?? ""}
			disabled={!renderer.evaluateBooleanExpression(question.enabledExpression, formData, null)}
			onValueChange={() => null}
		/>
	</Row>;
}

function renderPhoneNumber(question: ComposedQuestion, renderer: FormsRenderer, formData: FormData, layoutCustom: LayoutCustomProperties): JSX.Element {
	return <Row id={question.name}>
		<MuiPhoneNumber
			label={question.label}
			placeholder={question.defaultValue ?? ""}
			disabled={!renderer.evaluateBooleanExpression(question.enabledExpression, formData, null)}
			defaultCountry={"nl"}
			onChange={() => null}
		/>
	</Row>;
}

function renderStartAdornment(question: ComposedQuestion): JSX.Element | null {
	switch (question.unit) {
		case "euro":
			return <InputAdornment position="start">€</InputAdornment>;
		default:
			return null;
	}
}

function renderEndAdornment(question: ComposedQuestion): JSX.Element | null {
	switch (question.unit) {
		case "meter":
			return <InputAdornment position="end">m</InputAdornment>;
		case "meter2":
			return <InputAdornment position="end">m²</InputAdornment>;
		case "meter3":
			return <InputAdornment position="end">m³</InputAdornment>;
		case "percent":
			return <InputAdornment position="end">%</InputAdornment>;
		default:
			return null;
	}
}

function getInputType(question: ComposedQuestion) {
	switch (question.valueType) {
		case "integer":
		case "decimal":
		case "phoneNumber":
			return "number";
		default:
			return null;
	}
}

function RenderDropdown({question, renderer, formData, layoutCustom}: {question: ComposedQuestion; renderer: FormsRenderer; formData: FormData; layoutCustom: LayoutCustomProperties}): JSX.Element {
	const [selectedValue, setSelectedValue] = useState(() => getDefaultValue(question, formData) ?? "");
	const [referencedData, setReferencedData] = useState<object>({});
	
	useEffect(() => {
		const references = NameExtractor.extractNames(question);
		references.map(it =>
			formData.onValueChange(it, (name, value) =>
				setReferencedData({...referencedData, [name]: value})));
		
		// todo unregister
	});
	
	return <Row id={question.name}>
		<FormControl style={{flexGrow: 1}}>
			<InputLabel id={`${question.name}-label`}>{question.label}</InputLabel>
			<Select
				labelId={`${question.name}-label`}
				label={question.label}
				variant={"standard"}
				value={selectedValue}
				disabled={!renderer.evaluateBooleanExpression(question.enabledExpression, formData, selectedValue)}
				onChange={it => {
					setSelectedValue(it.target.value);
					
					formData.setValue(question.name, question.mapping, it.target.value);
				}}
			>
				{!(question.valueOptions ?? []).some(it => it.value === "")
					?<MenuItem key={"null"} value={""} hidden={true} style={{display: "none"}}></MenuItem>
					: undefined}
				
				{(question.valueOptions ?? []).map(it =>
					<MenuItem key={it.value} value={it.value}>{it.display}</MenuItem>
				)}
			</Select>
		</FormControl>
	</Row>;
}

function RenderRadioButtonList({question, renderer, formData, layoutCustom}: {question: ComposedQuestion; renderer: FormsRenderer; formData: FormData; layoutCustom: LayoutCustomProperties}): JSX.Element {
	const [selectedValue, setSelectedValue] = useState(() => getDefaultValue(question, formData) ?? "");
	const [referencedData, setReferencedData] = useState<object>({});
	
	useEffect(() => {
		const references = NameExtractor.extractNames(question);
		references.map(it =>
			formData.onValueChange(it, (name, value) =>
				setReferencedData({...referencedData, [name]: value})));
		
		// todo unregister
	});
	
	const disabled = !renderer.evaluateBooleanExpression(question.enabledExpression, formData, selectedValue);
	
	return <Row id={question.name}>
		<FormControl style={{flexGrow: 1}}>
			<FormLabel id={`${question.name}-label`}>{question.label}</FormLabel>
			<RadioGroup
				defaultValue={question.defaultValue} // todo expression
				name={question.name}
				value={selectedValue}
				onChange={it => {
					setSelectedValue(it.target.value);
					
					formData.setValue(question.name, question.mapping, it.target.value);
				}}
			>
				{!(question.valueOptions ?? []).some(it => it.value === "")
					? <FormControlLabel key={"null"} value={""} control={<Radio />} hidden={true} style={{display: "none"}} label={""}/>
					: undefined}
				
				{(question.valueOptions ?? []).map(it =>
					<FormControlLabel key={it.value} value={it.value} control={<Radio />} label={it.display} disabled={disabled}/>
				)}
			</RadioGroup>
		</FormControl>
	</Row>;
}

function getDefaultValue(question: ComposedQuestion, formData: FormData): string | null {
	const value = formData.findValueByName(question.name);
	if (value)
		return value;
	
	for (const mapping of question.mapping) {
		const value = formData.findValueByPath(mapping);
		if (value)
			return value;
	}
	
	return question.defaultValue;
}

function renderCheckBox(question: ComposedQuestion, renderer: FormsRenderer, formData: FormData, layoutCustom: LayoutCustomProperties): JSX.Element {
	return <Row id={question.name}>
		<FormControlLabel control={<Checkbox
			// defaultChecked={question.defaultValue === "true"}
			disabled={!renderer.evaluateBooleanExpression(question.enabledExpression, formData, null)}/>
		} label={renderLabel(question, layoutCustom)}/>
		
		<div style={{flexGrow: 1}} />
		{question.extraDisplay && <QuestionExtraDisplay extraDisplay={question.extraDisplay}/>}
		{question.description && <QuestionDescription description={question.description}/>}
	</Row>;
}

function QuestionExtraDisplay(props: {extraDisplay: string}) {
	return <Typography>
		{props.extraDisplay}
	</Typography>;
}

function QuestionDescription(props: {description: string}) {
	return <Tooltip title={props.description}>
		<Info />
	</Tooltip>;
}

function renderLabel(question: ComposedQuestion, layoutCustom: LayoutCustomProperties) {
	if (layoutCustom.labelContentType === "html") {
		return <span dangerouslySetInnerHTML={{__html: question.label}} />;
	} else {
		return question.label;
	}
}

interface RowProps {
	readonly id: string;
	readonly children: ReactNode;
}

function Row(props: RowProps) {
	return <div id={props.id} style={{
		display: "flex",
		flexDirection: "row",
		alignItems: "center",
		width: "100%",
		minWidth: "0px",
	}}>
		{props.children}
	</div>;
}

interface DynamicFormsTextFieldProps {
	readonly question: ComposedQuestion;
	readonly renderer: FormsRenderer;
	readonly formData: FormData;
	readonly layoutCustom: LayoutCustomProperties;
	readonly inputType?: HTMLInputTypeAttribute;
	readonly multiline?: boolean;
}

function DynamicFormsTextField({question, renderer, formData, layoutCustom, inputType, multiline}: DynamicFormsTextFieldProps) {
	const [error, setError] = useState<string>();
	const [selectedValue, setSelectedValue] = useState(() => getDefaultValue(question, formData) ?? "");
	const [referencedData, setReferencedData] = useState<object>({});
	
	useEffect(() => {
		const references = NameExtractor.extractNames(question);
		references.map(it =>
			formData.onValueChange(it, (name, value) =>
				setReferencedData({...referencedData, [name]: value})));
		
		// todo unregister
	});
	
	return <Row id={question.name}>
		<FormControl style={{width: "100%"}}>
			<TextField
				label={question.label}
				placeholder={question.defaultValue ?? ""}
				value={selectedValue}
				disabled={!renderer.evaluateBooleanExpression(question.editableExpression, formData, selectedValue)}
				type={inputType ?? getInputType(question) ?? undefined}
				InputProps={{
					startAdornment: renderStartAdornment(question),
					endAdornment: renderEndAdornment(question),
				}}
				InputLabelProps={{
					shrink: question.defaultValue ? true : undefined,
				}}
				required={renderer.evaluateBooleanExpression(question.requiredExpression, formData, selectedValue)}
				onInvalid={event => {
					const validity: Validity = (event.target as any).validity;
					const validationMessage: string = (event.target as any).validationMessage;
					if (validity.valid)
						setError(undefined);
					else if (validity.valueMissing) {
						setError(question.missingRequiredValueErrorMessage ?? validationMessage);
					} else
						setError(validationMessage);
					event.preventDefault();
				}}
				onChange={event => {
					setSelectedValue(event.target.value);
					setError(undefined);
					
					formData.setValue(question.name, question.mapping, event.target.value);
				}}
				multiline={multiline}
				error={!!error}
				helperText={error ?? text(question.description)}
				variant={"standard"}
				style={{width: "100%"}}
			/>
		</FormControl>
	</Row>;
}

function text(text: string | null | undefined) {
	if (!text)
		return undefined;
	return renderLines(text);
}

interface Validity {
	readonly badInput: boolean;
	readonly customError: boolean;
	readonly patternMismatch: boolean;
	readonly rangeOverflow: boolean;
	readonly rangeUnderflow: boolean;
	readonly stepMismatch: boolean;
	readonly tooLong: boolean;
	readonly tooShort: boolean;
	readonly typeMismatch: boolean;
	readonly valid: boolean;
	readonly valueMissing: boolean;
}

